import {
  createContext,
  ReactNode,
  useState,
  useCallback,
  useContext,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  DownloadSignedUrlFile,
  DownloadParams,
} from '@/lib/download';

export type DownloadStatus = 'pending' | 'completed' | 'failed';

export interface DownloadInfo {
  id: string;
  title: string;
  status: DownloadStatus;
  progress?: number;
  // True while the backend builds one zip of the selection, before any
  // file is handed to the browser.
  preparing?: boolean;
  // undefined while pending/completed; null means the /api/download
  // request itself failed before any signed-url files came back, so a
  // retry has to redo the whole request; an array is the subset that
  // failed to actually save.
  files?: DownloadSignedUrlFile[] | null;
  params: DownloadParams;
}

interface DownloadContextType {
  managerOpen: boolean;
  setManagerOpen: (open: boolean) => void;
  list: DownloadInfo[];
  loading: boolean;
  addDownload: (title: string, params: DownloadParams) => string;
  updateDownloadById: (
    id: string,
    patch: Partial<DownloadInfo>,
  ) => void;
  removeDownload: (id: string) => void;
}

export const useDownloadContext = () => {
  const context = useContext(DownloadContext);
  if (!context) {
    throw new Error(
      'useDownloadContext must be used within a DownloadContextProvider',
    );
  }
  return context;
};

const DownloadContext = createContext<
  DownloadContextType | undefined
>(undefined);

export const DownloadContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [managerOpen, setManagerOpen] = useState(false);
  const [list, setList] = useState<DownloadInfo[]>([]);
  const loading = list.some((d) => d.status === 'pending');

  const addDownload = useCallback(
    (title: string, params: DownloadParams) => {
      const id = `download-${uuidv4()}`;
      setManagerOpen(true);
      setList((prev) => [
        ...prev,
        { id, title, status: 'pending', progress: 0, params },
      ]);
      return id;
    },
    [],
  );

  const updateDownloadById = useCallback(
    (id: string, patch: Partial<DownloadInfo>) => {
      setList((prev) =>
        prev.map((download) =>
          download.id === id ? { ...download, ...patch } : download,
        ),
      );
    },
    [],
  );

  const removeDownload = useCallback((id: string) => {
    setList((prev) => prev.filter((download) => download.id !== id));
  }, []);

  return (
    <DownloadContext.Provider
      value={{
        managerOpen,
        setManagerOpen,
        list,
        loading,
        addDownload,
        updateDownloadById,
        removeDownload,
      }}
    >
      {children}
    </DownloadContext.Provider>
  );
};

export { DownloadContext };
export default DownloadContextProvider;
